// Service MetaMask pour SnowCash
import { ethers } from 'ethers';

class MetaMaskService {
  constructor() {
    this.provider = null;
    this.signer = null;
    this.isConnected = false;
  }

  // Vérifier si MetaMask est installé
  isMetaMaskInstalled() {
    return typeof window !== 'undefined' && typeof window.ethereum !== 'undefined';
  }

  // Initialiser le provider
  async initProvider() {
    if (!this.isMetaMaskInstalled()) {
      throw new Error('MetaMask n\'est pas installé');
    }

    this.provider = new ethers.BrowserProvider(window.ethereum);
    return this.provider;
  }

  // Connecter MetaMask
  async connect() {
    try {
      if (!this.isMetaMaskInstalled()) {
        throw new Error('MetaMask n\'est pas installé');
      }

      // Vérifier d'abord si on a déjà des comptes connectés
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      
      if (accounts.length === 0) {
        // Aucun compte connecté, demander la permission
        try {
          await window.ethereum.request({ method: 'eth_requestAccounts' });
        } catch (error) {
          if (error.code === -32002) {
            throw new Error('Une demande de connexion est déjà en cours. Veuillez vérifier MetaMask et approuver la connexion.');
          }
          throw error;
        }
      }
      
      // Initialiser le provider
      await this.initProvider();
      this.signer = await this.provider.getSigner();
      
      // Récupérer l'adresse
      const address = await this.signer.getAddress();
      this.isConnected = true;

      return {
        success: true,
        address,
        chainId: await this.getChainId()
      };
    } catch (error) {
      console.error('Erreur connexion MetaMask:', error);
      throw error;
    }
  }

  // Signer un message pour l'authentification
  async signMessage(message) {
    try {
      if (!this.signer) {
        throw new Error('MetaMask non connecté');
      }

      const signature = await this.signer.signMessage(message);
      return signature;
    } catch (error) {
      console.error('Erreur signature:', error);
      throw error;
    }
  }

  // Récupérer l'adresse actuelle
  async getCurrentAddress() {
    try {
      if (!this.signer) {
        await this.connect();
      }
      return await this.signer.getAddress();
    } catch (error) {
      console.error('Erreur récupération adresse:', error);
      throw error;
    }
  }

  // Récupérer le chainId
  async getChainId() {
    try {
      if (!this.provider) {
        await this.initProvider();
      }
      const network = await this.provider.getNetwork();
      return network.chainId;
    } catch (error) {
      console.error('Erreur récupération chainId:', error);
      return null;
    }
  }

  // Récupérer le solde ETH
  async getBalance(address) {
    try {
      if (!this.provider) {
        await this.initProvider();
      }
      
      const balance = await this.provider.getBalance(address);
      return ethers.formatEther(balance);
    } catch (error) {
      console.error('Erreur récupération solde:', error);
      return '0';
    }
  }

  // Vérifier si on est sur le bon réseau
  async checkNetwork() {
    try {
      const chainId = await this.getChainId();
      
      // Ethereum Mainnet = 1, Arbitrum = 42161
      const supportedChains = [1, 42161];
      
      return {
        chainId,
        isSupported: supportedChains.includes(Number(chainId)),
        networkName: this.getNetworkName(chainId)
      };
    } catch (error) {
      console.error('Erreur vérification réseau:', error);
      return { chainId: null, isSupported: false, networkName: 'Inconnu' };
    }
  }

  // Obtenir le nom du réseau
  getNetworkName(chainId) {
    const networks = {
      1: 'Ethereum Mainnet',
      42161: 'Arbitrum One',
      5: 'Goerli Testnet',
      11155111: 'Sepolia Testnet'
    };
    
    return networks[Number(chainId)] || `Réseau ${chainId}`;
  }

  // Changer de réseau
  async switchToNetwork(chainId) {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${chainId.toString(16)}` }],
      });
      return true;
    } catch (error) {
      console.error('Erreur changement réseau:', error);
      return false;
    }
  }

  // Écouter les changements de compte
  onAccountsChanged(callback) {
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', (accounts) => {
        if (accounts.length === 0) {
          this.disconnect();
        }
        callback(accounts);
      });
    }
  }

  // Écouter les changements de réseau
  onChainChanged(callback) {
    if (window.ethereum) {
      window.ethereum.on('chainChanged', (chainId) => {
        callback(parseInt(chainId, 16));
      });
    }
  }

  // Déconnecter
  disconnect() {
    this.provider = null;
    this.signer = null;
    this.isConnected = false;
  }

  // Réinitialiser l'état MetaMask
  async resetState() {
    try {
      this.disconnect();
      
      // Attendre un peu pour laisser MetaMask se réinitialiser
      await new Promise(resolve => setTimeout(resolve, 500));
      
      return true;
    } catch (error) {
      console.error('Erreur reset MetaMask:', error);
      return false;
    }
  }

  // Vérifier l'état des permissions
  async checkPermissions() {
    try {
      if (!this.isMetaMaskInstalled()) {
        return { hasPermissions: false, accounts: [] };
      }

      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      
      return {
        hasPermissions: accounts.length > 0,
        accounts,
        currentAccount: accounts[0] || null
      };
    } catch (error) {
      console.error('Erreur vérification permissions:', error);
      return { hasPermissions: false, accounts: [] };
    }
  }

  // Générer un message de signature unique
  generateAuthMessage(address, timestamp = Date.now()) {
    return `SnowCash Trading Bot - Authentification\n\nAdresse: ${address}\nTimestamp: ${timestamp}\n\nEn signant ce message, vous autorisez l'accès à votre compte SnowCash.`;
  }

  // Processus d'authentification complet
  async authenticate() {
    try {
      // 1. Connecter MetaMask
      const connectionResult = await this.connect();
      
      if (!connectionResult.success) {
        throw new Error('Échec de la connexion MetaMask');
      }

      // 2. Vérifier le réseau
      const networkCheck = await this.checkNetwork();
      
      if (!networkCheck.isSupported) {
        console.warn(`Réseau non supporté: ${networkCheck.networkName}`);
        // On continue quand même pour l'authentification
      }

      // 3. Générer et signer le message
      const address = connectionResult.address;
      const timestamp = Date.now();
      const message = this.generateAuthMessage(address, timestamp);
      const signature = await this.signMessage(message);

      return {
        success: true,
        address,
        signature,
        message,
        timestamp,
        chainId: networkCheck.chainId,
        networkName: networkCheck.networkName
      };
    } catch (error) {
      console.error('Erreur authentification complète:', error);
      throw error;
    }
  }
}

export default new MetaMaskService();
